import React, {Component, ReactNode} from 'react';
import {onboardingSessions} from './service/api/api_session';
import {
    getOnboardingState,
    isFormDataChanged,
    setOnboardingStep,
    skipOnboardingProcess
} from './service/completionState';
import {ModalTypes} from './enums';
import {getServerTexts} from './texts';
import {getModalType} from './utils';
import {companyFormStore} from './store/OB_Company_Store';
import {bankAccountFormStore} from './store/OB_BankAccount_Store';
import {questionnaireStore} from './store/OB_Questionnaire_Store';
import {SkipOnboardingDialog} from './components/Dialogs';
import {FormChangeAlert} from './components/FormChangesAlert';
import {default_OB_context} from './constants';
import {OnboardingCtxModel} from './context.model';

type Props = {
    children?: ReactNode;
};

async function clearOnboardingStores() {
    companyFormStore.resetStore();
    bankAccountFormStore.resetStore();
    questionnaireStore.resetStore();

    await onboardingSessions.clearAllForms();
}

export const OnboardingCtx = React.createContext<OnboardingCtxModel>(default_OB_context);

export class OnboardingCtxProvider extends Component<Props, OnboardingCtxModel> {
    constructor(props) {
        super(props);

        this.state = {
            OB_is_open: false,
            OB_dialog_type: ModalTypes.hidden,
            open_OB_dialog: this.onOnboardingOpen,
            close_OB_dialog: this.onOnboardingClose,
            leave_OB_process: this.leaveOnboarding,

            dataCheck_callback: undefined,
            check_unsaved_data: this.checkForUnsavedData,
            close_save_data_alert: this.closeSaveDataAlert,
            data_alert_is_open: false,

            open_skip_OB_alert: this.openSkipAlert,
            close_skip_OB_alert: this.closeSkipAlert,
            skip_OB_is_open: false,

            OB_process_start: this.startOnboarding,
            OB_process_skip: this.skipOnboarding
        };
    }

    /* Request Texts initial data, open OB dialog according of the OB step */
    onOnboardingOpen = async () => {
        await getServerTexts();
        const dialogType = await getModalType();

        this.setState({OB_dialog_type: dialogType, OB_is_open: true});
    };

    /* OB dialog onClose callback */
    onOnboardingClose = async (callback?: () => void) => {
        if (this.state.OB_is_open) {
            // If there is no data to save -> leave onboarding dialog

            if (callback) {
                this.checkForUnsavedData(async () => {
                    await callback?.();
                    this.leaveOnboarding();
                });
                return;
            }
            this.leaveOnboarding();
        }
    };

    /* Running the leaving OB Process, clearing store and sessions data */
    leaveOnboarding = async () => {
        // close all dialog related to Onboarding
        this.setState({OB_is_open: false});

        await clearOnboardingStores();
    };

    /* Running a check of the current open OB Data form (i.e. COMPDATA | CASH_BANKS) */
    checkForUnsavedData = (callback?: () => void) => {
        const hasUnsavedData = isFormDataChanged();

        if (hasUnsavedData) {
            this.setState({data_alert_is_open: true, dataCheck_callback: callback});
        } else {
            callback();
        }
    };

    /* Close warning alert */
    closeSaveDataAlert = () =>
        this.setState({data_alert_is_open: false, dataCheck_callback: undefined});

    /* Open Warning message about skipping the OB process */
    openSkipAlert = () => this.setState({skip_OB_is_open: true});

    /*  Hide warning message of the skipping OB process for current company */
    closeSkipAlert = () => this.setState({skip_OB_is_open: false});

    /*  Show onboarding dialog, if the user continue OB process, dialog open the last visited OB step */
    startOnboarding = async () => {
        const {OB_step} = getOnboardingState();

        if (!OB_step) {
            await setOnboardingStep(OB_step || '0');
        }

        this.setState({OB_dialog_type: ModalTypes.wizardSetup});
    };

    /* Hide OB Dialog and don't show it anymore for current company */
    skipOnboarding = async () => {
        await skipOnboardingProcess();
        this.closeSkipAlert();
        await this.onOnboardingClose();
    };

    render() {
        const {skip_OB_is_open, data_alert_is_open} = this.state;
        return (
            <OnboardingCtx.Provider value={this.state}>
                {this.props.children}

                <SkipOnboardingDialog
                    isOpen={skip_OB_is_open}
                    onClose={this.closeSkipAlert}
                    onDecline={this.closeSkipAlert}
                    onApprove={this.skipOnboarding}
                />

                <FormChangeAlert
                    isOpen={data_alert_is_open}
                    onClose={this.closeSaveDataAlert}
                    onApprove={this.state.dataCheck_callback} // will be different in different cases
                />
            </OnboardingCtx.Provider>
        );
    }
}
